import React, { useState } from 'react';
import {
  Camera,
  Settings,
  X,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';

interface ScreenshotToolProps {
  onTakeScreenshot: () => Promise<string>;
  isCapturing: boolean;
}

export const ScreenshotTool: React.FC<ScreenshotToolProps> = ({
  onTakeScreenshot,
  isCapturing,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [lastScreenshot, setLastScreenshot] = useState<string | null>(null);

  const { screenshotSettings, updateScreenshotSettings } = useLivePreviewStore();

  const handleScreenshot = async () => {
    const dataUrl = await onTakeScreenshot();
    if (dataUrl) {
      setLastScreenshot(dataUrl);
    }
  };

  return (
    <div className="relative">
      {/* Main button */}
      <div className="flex items-center gap-1">
        <button
          onClick={handleScreenshot}
          disabled={isCapturing}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
            'bg-zinc-700 hover:bg-zinc-600',
            isCapturing && 'opacity-50 cursor-not-allowed'
          )}
          title="Take Screenshot (F12)"
        >
          <Camera size={16} />
          <span>Screenshot</span>
        </button>

        <button
          onClick={() => setShowSettings(!showSettings)}
          className={cn(
            'p-1.5 rounded hover:bg-zinc-700',
            showSettings && 'bg-zinc-700'
          )}
        >
          <Settings size={16} />
        </button>
      </div>

      {/* Settings dropdown */}
      {showSettings && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Screenshot Settings</span>
            <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-zinc-700 rounded">
              <X size={14} />
            </button>
          </div>

          {/* Format */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Format</label>
            <div className="flex gap-1">
              {(['png', 'jpeg', 'webp'] as const).map((format) => (
                <button
                  key={format}
                  onClick={() => updateScreenshotSettings({ format })}
                  className={cn(
                    'flex-1 px-2 py-1 rounded text-xs uppercase',
                    screenshotSettings.format === format
                      ? 'bg-blue-600'
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  )}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>

          {/* Quality (for jpeg/webp) */}
          {screenshotSettings.format !== 'png' && (
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">
                Quality: {Math.round(screenshotSettings.quality * 100)}%
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={screenshotSettings.quality}
                onChange={(e) =>
                  updateScreenshotSettings({ quality: parseFloat(e.target.value) })
                }
                className="w-full"
              />
            </div>
          )}

          {/* Scale */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">
              Scale: {screenshotSettings.scale}x
            </label>
            <div className="flex gap-1">
              {[1, 2, 4].map((scale) => (
                <button
                  key={scale}
                  onClick={() => updateScreenshotSettings({ scale })}
                  className={cn(
                    'flex-1 px-2 py-1 rounded text-xs',
                    screenshotSettings.scale === scale
                      ? 'bg-blue-600'
                      : 'bg-zinc-700 hover:bg-zinc-600'
                  )}
                >
                  {scale}x
                </button>
              ))}
            </div>
          </div>

          {/* Filename */}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Filename prefix</label>
            <input
              type="text"
              value={screenshotSettings.filename}
              onChange={(e) =>
                updateScreenshotSettings({ filename: e.target.value })
              }
              className="w-full px-2 py-1 bg-zinc-700 border border-zinc-600 rounded text-sm"
            />
          </div>
        </div>
      )}

      {/* Preview of last screenshot */}
      {lastScreenshot && (
        <div className="absolute top-full right-0 mt-1 p-2 bg-zinc-800 border border-zinc-700 rounded shadow-lg">
          <img
            src={lastScreenshot}
            alt="Last screenshot"
            className="w-32 h-auto rounded"
          />
          <button
            onClick={() => setLastScreenshot(null)}
            className="absolute -top-2 -right-2 p-1 bg-zinc-700 rounded-full hover:bg-zinc-600"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};
